import { useQuery } from "@apollo/client";
import getYear from "date-fns/getYear";
import { useState } from "react";

import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import getFiscalQuarterText from "components/company/highlights/getFiscalQuarterText";
import ContentContainer from "components/company/sections/ContentContainer";
import ContentSectionBreadcrumbs from "components/company/sections/ContentSectionBreadcrumbs";
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import FinancialHighlightsSummary from "components/company/sections/summary/FinancialHighlightsSummary";
import StaticContentSection from "components/company/StaticContentSection";
import CurrentManagementReportAnchor from "components/company/v2/CurrentManagementReportAnchor";
import FinancialHighlightsContent from "components/portfolio/components/FinancialHighlightsContent";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";

import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";
import FinancialHighlightsQuery from "lib/queries/FinancialHighlightsQuery";
import type {
  IFinancialHighlightsQuery,
  IFinancialHighlightsQueryVariables,
} from "lib/queries/__generated__/FinancialHighlightsQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { FormattedMessage } from "react-intl";
import { SquareArrowOutUpRightIcon } from "lucide-react";

interface IBrazilianIssuerFinancialHighlightsCollapsibleProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  isExpanded: boolean;
  isPremium: boolean;
  onToggleCollapsed: () => void;
}

export default function BrazilianIssuerFinancialHighlightsCollapsible({
  issuer,
  isExpanded,
  isPremium,
  onToggleCollapsed,
}: IBrazilianIssuerFinancialHighlightsCollapsibleProps) {
  const [filterByFinancialHighlightsId, setFilterByFinancialHighlightsId] = useState<string | null>(null);

  const { data, previousData } = useQuery<IFinancialHighlightsQuery, IFinancialHighlightsQueryVariables>(
    FinancialHighlightsQuery,
    {
      variables: {
        issuerId: issuer._id,
        financialHighlightsId: filterByFinancialHighlightsId,
      },
    },
  );

  const highlights = data ?? previousData;
  const hasFinancialHighlights = highlights && highlights.financialHighlights;
  const hasManagementReport = issuer.lastQuarterlyManagementReport || issuer.lastYearlyManagementReport;

  return (
    <>
      {(!isPremium || !hasFinancialHighlights) && hasManagementReport && (
        <StaticContentSection primaryColor={palette.fiord} className="flex font-aeonik">
          <div className="w-full">
            <div>
              <ContentSectionHeading>
                <FormattedMessage defaultMessage="Destaques Financeiros" id="gGwvIs" />
              </ContentSectionHeading>
              <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Último Trimestre"]} />
            </div>

            <div className="mt-2 w-full space-y-1 rounded-xl bg-slate-100 p-4">
              <CurrentManagementReportAnchor
                lastYearlyManagementReport={issuer.lastYearlyManagementReport}
                lastQuarterlyManagementReport={issuer.lastQuarterlyManagementReport}
                reportSource={highlights?.financialHighlights?.reportSource}
                className="font-medium text-blue-600 hover:underline"
              />

              <div className="flex items-center space-x-1">
                <span className="flex text-sm font-medium italic text-slate-500">
                  <FormattedMessage defaultMessage="Último publicado" id="Xlvgxf" />
                </span>

                <CurrentManagementReportAnchor
                  lastYearlyManagementReport={issuer.lastYearlyManagementReport}
                  lastQuarterlyManagementReport={issuer.lastQuarterlyManagementReport}
                  reportSource={highlights?.financialHighlights?.reportSource}
                >
                  <SquareArrowOutUpRightIcon className="w-4 text-slate-500" strokeWidth={2} />
                </CurrentManagementReportAnchor>
              </div>
            </div>
          </div>
        </StaticContentSection>
      )}

      {isPremium && hasFinancialHighlights && (
        <CollapsibleContentSection
          title="Destaques Financeiros"
          isExpanded={isExpanded}
          onToggleCollapsed={onToggleCollapsed}
          primaryColor={palette.fiord}
          renderSubtitle={() => {
            if (!highlights || !highlights.financialHighlights) {
              return null;
            }

            const { fiscalPeriodQuarter, fiscalPeriodDate } = highlights.financialHighlights.financialHighlights;
            const fiscalYear = getYear(parseDateYmd(fiscalPeriodDate));

            return (
              <ContentSectionBreadcrumbs
                segments={["Informações Financeiras", `${getFiscalQuarterText(fiscalPeriodQuarter)} • ${fiscalYear}`]}
              />
            );
          }}
          renderContentSummary={() => {
            if (!highlights || !highlights.financialHighlights) {
              return null;
            }

            return (
              <FinancialHighlightsSummary
                lastFinancialHighlights={highlights.financialHighlights.financialHighlights}
              />
            );
          }}
          renderContent={() => {
            if (!highlights || !highlights.financialHighlights) {
              return null;
            }

            const candidateHighlights =
              highlights.financialHighlights.availableHighlights.length > 0
                ? highlights.financialHighlights.availableHighlights[0]._id
                : undefined;

            return (
              <ContentContainer>
                <div className="p-6">
                  <div className="flex flex-col space-y-2 sm:flex-row sm:justify-between">
                    <div>
                      <h1
                        className="m-0 inline-flex text-2xl font-bold tracking-wider"
                        style={{
                          color: palette.bigStone,
                        }}
                      >
                        <FormattedMessage defaultMessage="Destaques Financeiros" id="gGwvIs" />
                      </h1>

                      <div className="mt-1">
                        <time
                          className="text-sm font-medium tracking-wider"
                          dateTime={highlights.financialHighlights.financialHighlights.fiscalPeriodDate}
                          style={{
                            color: palette.blueBayoux,
                          }}
                        >
                          {getFiscalQuarterText(highlights.financialHighlights.financialHighlights.fiscalPeriodQuarter)}
                        </time>
                      </div>

                      <div className="mt-3 grid auto-cols-max grid-flow-col gap-x-1 text-sm">
                        <CurrentManagementReportAnchor
                          lastQuarterlyManagementReport={issuer.lastQuarterlyManagementReport}
                          lastYearlyManagementReport={issuer.lastYearlyManagementReport}
                          reportSource={highlights.financialHighlights.reportSource}
                          className="font-medium text-blue-600 no-underline"
                        />
                      </div>
                    </div>

                    <Select
                      onValueChange={(value) => setFilterByFinancialHighlightsId(value ?? null)}
                      defaultValue={candidateHighlights}
                    >
                      <SelectTrigger className="w-full rounded-xl border-slate-200 bg-white font-aeonik sm:w-48">
                        <SelectValue placeholder="Escolha o Trimestre" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg border border-slate-200 text-sm">
                        {highlights.financialHighlights.availableHighlights.map((highlights) => (
                          <SelectItem
                            key={`selectBoxYear/${highlights._id}`}
                            className="cursor-pointer rounded-lg text-sm"
                            value={highlights._id}
                          >
                            {highlights.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-4">
                    <div className="text-base tracking-wider text-gray-700">
                      <FinancialHighlightsContent
                        key={highlights.financialHighlights.financialHighlights._id}
                        content={highlights.financialHighlights.financialHighlights.highlights}
                      />
                    </div>
                  </div>
                </div>
              </ContentContainer>
            );
          }}
        />
      )}
    </>
  );
}
